import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WorkbenchField, WorkbenchNotice } from '@ndrc/ui';
import { useDesk } from '../desk/context';
import { t } from '../messages';
import { readSession, saveSession, usePracticeRequest } from './usePracticeRequest';
import type { Criterion } from './model';
export function PracticeReview({
  classId,
  submissionId,
  rubric,
  refresh,
}: {
  classId: string;
  submissionId: string;
  rubric: Criterion[];
  refresh: () => Promise<void>;
}) {
  const { me } = useDesk();
  const cache = useQueryClient();
  const scope = `ndrc-practice:${me.id}:${classId}:${submissionId}:review`;
  const [draft, setDraft] = useState(() =>
    readSession<{ note: string; scores: Record<string, string> }>(scope, { note: '', scores: {} }),
  );
  const [stored, setStored] = useState(true),
    [notice, setNotice] = useState('');
  const request = usePracticeRequest(`${scope}:request`);
  function change(next: typeof draft) {
    setDraft(next);
    setStored(saveSession(scope, next));
    setNotice('');
  }
  async function review(published: boolean) {
    const result = await request.execute('practice.review', {
      classId,
      submissionId,
      note: draft.note,
      scores: rubric.map((c) => ({
        criterionId: c.id,
        points: draft.scores[c.id]?.trim() ? Number(draft.scores[c.id]) : null,
      })),
      published,
    });
    if (result !== undefined) {
      setNotice(t('practice.reviewSaved'));
      await cache.invalidateQueries({ queryKey: ['core', 'practice.reviews', classId] });
      await refresh();
    }
  }
  const invalid =
    !draft.note.trim() ||
    rubric.some(
      (c) =>
        draft.scores[c.id]?.trim() &&
        (c.maxPoints === null ||
          !Number.isFinite(Number(draft.scores[c.id])) ||
          Number(draft.scores[c.id]) < 0 ||
          Number(draft.scores[c.id]) > c.maxPoints),
    );
  return (
    <section className="practice-review">
      <h3>{t('practice.correct')}</h3>
      <p>{t('practice.correctHint')}</p>
      {(!stored || !request.stored) && (
        <WorkbenchNotice error>{t('practice.storageError')}</WorkbenchNotice>
      )}
      {request.error && <WorkbenchNotice error>{request.error}</WorkbenchNotice>}
      {notice && <WorkbenchNotice>{notice}</WorkbenchNotice>}
      <fieldset disabled={request.busy || Boolean(request.pending)}>
        <WorkbenchField label={t('practice.feedback')}>
          <textarea
            value={draft.note}
            maxLength={10000}
            rows={5}
            onChange={(event) => change({ ...draft, note: event.target.value })}
          />
        </WorkbenchField>
        {rubric.map((c) => (
          <WorkbenchField key={c.id} label={c.label}>
            {c.maxPoints === null ? (
              <span>{t('practice.noGrade')}</span>
            ) : (
              <input
                type="number"
                min={0}
                max={c.maxPoints}
                step="any"
                placeholder={t('practice.noGrade')}
                value={draft.scores[c.id] ?? ''}
                onChange={(event) =>
                  change({ ...draft, scores: { ...draft.scores, [c.id]: event.target.value } })
                }
              />
            )}
          </WorkbenchField>
        ))}
        <div className="lab-actions">
          <button
            type="button"
            disabled={invalid}
            onClick={() => {
              void review(false);
            }}
          >
            {t('practice.savePrivate')}
          </button>
          <button
            type="button"
            className="lab-primary"
            disabled={invalid}
            onClick={() => {
              void review(true);
            }}
          >
            {t('practice.publishReview')}
          </button>
        </div>
      </fieldset>
      {request.pending && (
        <WorkbenchNotice>
          <p>{t('practice.pending')}</p>
          <button
            type="button"
            disabled={request.busy}
            onClick={() => {
              void review(false);
            }}
          >
            {t('practice.retry')}
          </button>
        </WorkbenchNotice>
      )}
    </section>
  );
}
